import React from 'react';
import PropTypes from 'prop-types';

import {
  Box, Divider, Flex, Heading, Image, Link, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useIsMobile} from '@amblique/king-living/app/hooks/use-memoized-breakpoint';
import Breadcrumbs from '@amblique/king-living/app/components/breadcrumbs';

const BlogHeading = ({
  breadcrumbs = [],
  title,
  author,
  readTime,
  datePublished,
  description,
  shareLinks = [],
  ...props
}) => {
  const styles = useMultiStyleConfig('BlogHeading', {});
  const isMobile = useIsMobile();

  // Filter social icons - only items with url and image configured
  const socialIcons = shareLinks.filter((link) => link.url && link.image?.url);

  return (
    <Box {...styles.container} {...props}>
      {breadcrumbs.length > 0 && (
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      )}

      <Flex {...styles.articleContainer}>
        <Box {...styles.titleContainer}>
          <Heading {...styles.title}>{title}</Heading>
        </Box>

        <Flex {...styles.metadataShareContainer}>
          <Flex {...styles.metadataContainer}>
            <Flex {...styles.metadataRow}>
              {author && (
                <>
                  <Flex {...styles.metadataItem}>
                    <Text {...styles.metadataLabel}>Written by</Text>
                    <Text {...styles.metadataValue}>{author}</Text>
                  </Flex>
                  {(readTime || datePublished) && <Divider {...styles.metadataDivider} />}
                </>
              )}

              {!isMobile && readTime && (
                <>
                  <Text {...styles.metadataValueOnly}>{readTime}</Text>
                  {datePublished && <Divider {...styles.metadataDivider} />}
                </>
              )}

              {datePublished && (
                <Flex {...styles.metadataItem}>
                  <Text {...styles.metadataLabel}>Date published</Text>
                  <Text {...styles.metadataValue}>{datePublished}</Text>
                </Flex>
              )}

              {isMobile && readTime && (
                <>
                  {datePublished && <Divider {...styles.metadataDivider} />}
                  <Text {...styles.metadataValueOnly}>{readTime}</Text>
                </>
              )}
            </Flex>
          </Flex>

          {socialIcons.length > 0 && (
            <Flex {...styles.shareContainer}>
              <Text {...styles.shareLabel}>Share this post</Text>
              <Divider {...styles.shareDivider} />
              <Flex {...styles.socialIcons}>
                {socialIcons.map((link) => (
                  <Link
                    key={link.sys?.id || link.url}
                    {...styles.socialIcon}
                    aria-label={link.text || 'Share'}
                    href={link.url}
                    isExternal={link.openInNewTab}
                  >
                    <Image
                      alt={link.image.title || link.text || 'Social icon'}
                      height="20px"
                      src={link.image.url}
                      width="20px"
                    />
                  </Link>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        <Divider {...styles.bottomDivider} />

        {description && (
          <Flex {...styles.descriptionContainer}>
            <Text {...styles.description}>{description}</Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

BlogHeading.displayName = 'BlogHeading';

BlogHeading.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
    })
  ),
  title: PropTypes.string.isRequired,
  author: PropTypes.string,
  readTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  datePublished: PropTypes.string,
  description: PropTypes.string,
  shareLinks: PropTypes.arrayOf(
    PropTypes.shape({
      sys: PropTypes.shape({id: PropTypes.string}),
      text: PropTypes.string,
      url: PropTypes.string,
      openInNewTab: PropTypes.bool,
      image: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }),
    })
  ),
};

export default BlogHeading;
